import { defineStore } from 'pinia'
import { useEditorStore } from './editor'

// Service registry for undo/redo operations
const serviceRegistry = new Map<string, any>()

export const registerService = (name: string, serviceInstance: any) => {
  serviceRegistry.set(name, serviceInstance)
}

export const unregisterService = (name: string) => {
  serviceRegistry.delete(name)
}

export interface Command {
  id: string
  description: string
  timestamp: number
  doActions: ActionDef[]      // Array of actions to execute
  undoActions: ActionDef[]    // Array of actions to undo
}

export interface ActionDef {
  target: string
  method: string
  args: any[]
}

interface UndoRedoState {
  history: Command[]
  currentIndex: number
  maxHistorySize: number
  isUndoRedoInProgress: boolean
  debounceTimeouts: Map<string, number>
}

export const useUndoRedoStore = defineStore('undoRedo', {
  state: (): UndoRedoState => ({
    history: [],
    currentIndex: -1,
    maxHistorySize: 50,
    isUndoRedoInProgress: false,
    debounceTimeouts: new Map()
  }),

  getters: {
    canUndo: (state) => state.currentIndex >= 0,
    canRedo: (state) => state.currentIndex < state.history.length - 1,
    historySize: (state) => state.history.length
  },

  actions: {
    getTarget(targetName: string) {
      const targetRegistry: Record<string, () => any> = {
        'editorStore': () => useEditorStore(),
        'custom': () => ({
          // Custom methods will be added here
        })
      }

      // Check service registry first
      if (serviceRegistry.has(targetName)) {
        return serviceRegistry.get(targetName)
      }

      const targetFactory = targetRegistry[targetName]
      if (!targetFactory) {
        throw new Error(`Unknown target: ${targetName}`)
      }
      return targetFactory()
    },

    async executeAction(action: ActionDef) {
      const target = this.getTarget(action.target)
      const method = target[action.method]

      if (typeof method !== 'function') {
        throw new Error(`Method '${action.method}' not found on target '${action.target}'`)
      }

      return await method.apply(target, action.args)
    },

    clearDebounceTimeout(key: string) {
      const timeoutId = this.debounceTimeouts.get(key)
      if (timeoutId) {
        clearTimeout(timeoutId)
        this.debounceTimeouts.delete(key)
      }
    },

    clearAllDebounceTimeouts() {
      this.debounceTimeouts.forEach((timeoutId) => clearTimeout(timeoutId))
      this.debounceTimeouts.clear()
    },

    addCommandToHistory(command: Command) {
      if (this.currentIndex < this.history.length - 1) {
        this.history = this.history.slice(0, this.currentIndex + 1)
      }

      this.history.push(command)
      this.currentIndex++

      if (this.history.length > this.maxHistorySize) {
        this.history.shift()
        this.currentIndex--
      }
    },

    record(
      description: string,
      doActions: ActionDef | ActionDef[],
      undoActions: ActionDef | ActionDef[],
      debounceKey?: string,
      debounceMs: number = 0
    ) {
      if (this.isUndoRedoInProgress) return

      // Convert single actions to arrays for consistency
      const doActionsArray = Array.isArray(doActions) ? doActions : [doActions]
      const undoActionsArray = Array.isArray(undoActions) ? undoActions : [undoActions]

      const command: Command = {
        id: `cmd_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        description,
        timestamp: Date.now(),
        doActions: doActionsArray,
        undoActions: undoActionsArray
      }

      if (debounceKey && debounceMs > 0) {
        this.clearDebounceTimeout(debounceKey)

        const timeoutId = window.setTimeout(() => {
          this.addCommandToHistory(command)
          this.debounceTimeouts.delete(debounceKey)
        }, debounceMs)

        this.debounceTimeouts.set(debounceKey, timeoutId)
      } else {
        this.addCommandToHistory(command)
      }
    },

    // Convenience method for recording multiple actions with better error handling
    recordMultiple(
      description: string,
      doActions: ActionDef[],
      undoActions: ActionDef[],
      debounceKey?: string,
      debounceMs: number = 0
    ) {
      if (doActions.length !== undoActions.length) {
        console.warn('doActions and undoActions arrays should have the same length for proper undo/redo behavior')
      }

      return this.record(description, doActions, undoActions, debounceKey, debounceMs)
    },

    async undo(): Promise<boolean> {
      if (!this.canUndo || this.isUndoRedoInProgress) return false

      const command = this.history[this.currentIndex]
      try {
        this.isUndoRedoInProgress = true

        // Execute undo actions in the order they were provided
        for (const action of command.undoActions) {
          await this.executeAction(action)
        }

        this.currentIndex--
        return true
      } catch (error) {
        console.error('Error during undo operation:', error)
        return false
      } finally {
        this.isUndoRedoInProgress = false
      }
    },

    async redo(): Promise<boolean> {
      if (!this.canRedo) return false

      this.currentIndex++
      const command = this.history[this.currentIndex]

      try {
        this.isUndoRedoInProgress = true

        // Execute do actions in order
        for (const action of command.doActions) {
          await this.executeAction(action)
        }

        return true
      } catch (error) {
        console.error('Error during redo operation:', error)
        this.currentIndex--
        return false
      } finally {
        this.isUndoRedoInProgress = false
      }
    },

    async handleKeyboardShortcut(event: KeyboardEvent): Promise<boolean> {
      if ((event.ctrlKey || event.metaKey) && !event.shiftKey && event.key === 'z') {
        event.preventDefault()
        return await this.undo()
      }

      if ((event.ctrlKey || event.metaKey) && event.key === 'y') {
        event.preventDefault()
        return await this.redo()
      }

      return false
    },

    clearHistory() {
      this.clearAllDebounceTimeouts()
      this.history = []
      this.currentIndex = -1
    }
  }
})
